import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/input";
import { X, Languages, Plus, Trash2, Edit2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";

interface CategoryTranslation {
  language: string;
  languageName: string;
  name: string;
  subcategories: Record<string, string>; // Changed to object mapping
}

interface CategoryTranslationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  categoryName: string;
  currentTranslations: {
    translatedName?: Record<string, string>;
    translatedSubCategories?: Record<string, Record<string, string>>; // Fixed structure
  };
  originalSubcategories?: string[];
  onSave: (translations: {
    translatedName: Record<string, string>;
    translatedSubCategories: Record<string, Record<string, string>>; // Fixed structure
  }) => void;
}

// Supported languages (excluding English)
const SUPPORTED_LANGUAGES = [
  { code: "it", name: "Italiano" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "pt", name: "Português" },
  { code: "ru", name: "Русский" },
  { code: "zh", name: "中文" },
  { code: "ja", name: "日本語" },
  { code: "ar", name: "العربية" },
  { code: "hi", name: "हिन्दी" },
  { code: "ko", name: "한국어" },
  { code: "nl", name: "Nederlands" },
  { code: "cs", name: "Čeština" },
];

const CategoryTranslationDialog: React.FC<CategoryTranslationDialogProps> = ({
  isOpen,
  onClose,
  categoryName,
  currentTranslations,
  originalSubcategories = [],
  onSave,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [translationName, setTranslationName] = useState<string>("");
  const [translationSubcategories, setTranslationSubcategories] = useState<Record<string, string>>({}); // Changed to object
  const [editingTranslation, setEditingTranslation] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const formRef = React.useRef<HTMLDivElement>(null);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedLanguage("");
      setTranslationName("");
      setTranslationSubcategories({});
      setEditingTranslation(null);
      setShowAddForm(false);
    }
  }, [isOpen]);

  // Build current translations list
  const existingTranslations: CategoryTranslation[] = SUPPORTED_LANGUAGES
    .filter(lang => currentTranslations.translatedName?.[lang.code])
    .map(lang => ({
      language: lang.code,
      languageName: lang.name,
      name: currentTranslations.translatedName?.[lang.code] || "",
      subcategories: currentTranslations.translatedSubCategories?.[lang.code] || {}, // Changed to object
    }));
  
  console.log("CategoryTranslationDialog - Current Translations:", currentTranslations);
  console.log("CategoryTranslationDialog - Existing Translations:", existingTranslations);

  const handleAddTranslation = () => {
    if (!selectedLanguage || !translationName.trim()) {
      return;
    }

    // Create new translation
    const newTranslatedName = {
      ...currentTranslations.translatedName,
      [selectedLanguage]: translationName.trim(),
    };

    // Filter out empty translations
    const cleanedSubcategories = Object.fromEntries(
      Object.entries(translationSubcategories).filter(([_, value]) => value.trim())
    );

    const newTranslatedSubCategories = {
      ...currentTranslations.translatedSubCategories,
      [selectedLanguage]: cleanedSubcategories,
    };

    // Reset form
    setSelectedLanguage("");
    setTranslationName("");
    setTranslationSubcategories({});
    setShowAddForm(false);

    // Save
    onSave({
      translatedName: newTranslatedName,
      translatedSubCategories: newTranslatedSubCategories,
    });
  };

  const handleUpdateTranslation = (langCode: string) => {
    if (!translationName.trim()) {
      return;
    }

    const newTranslatedName = {
      ...currentTranslations.translatedName,
      [langCode]: translationName.trim(),
    };

    // Filter out empty translations
    const cleanedSubcategories = Object.fromEntries(
      Object.entries(translationSubcategories).filter(([_, value]) => value.trim())
    );

    const newTranslatedSubCategories = {
      ...currentTranslations.translatedSubCategories,
      [langCode]: cleanedSubcategories,
    };

    // Reset editing state
    setEditingTranslation(null);
    setTranslationName("");
    setTranslationSubcategories({});

    // Save
    onSave({
      translatedName: newTranslatedName,
      translatedSubCategories: newTranslatedSubCategories,
    });
  };

  const handleDeleteTranslation = (langCode: string) => {
    const newTranslatedName = { ...currentTranslations.translatedName };
    const newTranslatedSubCategories = { ...currentTranslations.translatedSubCategories };

    delete newTranslatedName[langCode];
    delete newTranslatedSubCategories[langCode];

    onSave({
      translatedName: newTranslatedName,
      translatedSubCategories: newTranslatedSubCategories,
    });
  };

  const handleEditTranslation = (translation: CategoryTranslation) => {
    setEditingTranslation(translation.language);
    setTranslationName(translation.name);
    setTranslationSubcategories(translation.subcategories);
    setShowAddForm(true);
    
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);
  };

  const handleSubcategoryChange = (originalSubcat: string, translatedValue: string) => {
    setTranslationSubcategories(prev => ({
      ...prev,
      [originalSubcat]: translatedValue,
    }));
  };

  const handleRemoveSubcategoryTranslation = (originalSubcat: string) => {
    setTranslationSubcategories(prev => {
      const newState = { ...prev };
      delete newState[originalSubcat];
      return newState;
    });
  };

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    lang => !existingTranslations.some(t => t.language === lang.code) || editingTranslation === lang.code
  );

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingTranslation(null);
    setSelectedLanguage("");
    setTranslationName("");
    setTranslationSubcategories({});
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl flex-1 min-w-0">
              <Languages className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="truncate">Category Translations</span>
              <span className="hidden sm:inline">-</span>
              <span className="font-semibold truncate">{categoryName}</span>
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="flex-shrink-0 h-10 w-10 p-0 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 sm:space-y-6">
          {/* Existing Translations */}
          {existingTranslations.length > 0 && (
            <div>
              <h3 className="font-medium mb-3 text-sm sm:text-base">Existing Translations</h3>
              <div className="space-y-3">
                {existingTranslations.map((translation) => (
                  <div
                    key={translation.language}
                    className="p-3 sm:p-4 border rounded-lg bg-gray-50"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                      <Badge variant="outline" className="text-xs sm:text-sm w-fit">
                        {translation.languageName} ({translation.language})
                      </Badge>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditTranslation(translation)}
                          className="h-8 px-2 sm:px-3"
                        >
                          <Edit2 className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="ml-1 text-xs sm:text-sm">Edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTranslation(translation.language)}
                          className="h-8 px-2 sm:px-3"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />
                          <span className="ml-1 text-xs sm:text-sm">Delete</span>
                        </Button>
                      </div>
                    </div>
                    <div className="break-words">
                      <span className="text-xs sm:text-sm text-gray-600">Name: </span>
                      <span className="font-medium text-sm sm:text-base">{translation.name}</span>
                    </div>
                    {Object.keys(translation.subcategories).length > 0 && (
                      <div className="mt-3">
                        <span className="text-xs sm:text-sm text-gray-600">
                          Subcategory Translations ({Object.keys(translation.subcategories).length}):
                        </span>
                        <div className="space-y-1 mt-2 max-h-48 overflow-y-auto">
                          {Object.entries(translation.subcategories).map(([original, translated], idx) => (
                            <div key={idx} className="text-xs sm:text-sm bg-white p-2 sm:p-3 rounded border">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                                <span className="text-gray-500 break-words flex-1">{original}</span>
                                <span className="text-gray-400 hidden sm:inline">→</span>
                                <span className="font-medium break-words flex-1 text-blue-700">{translated}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add/Edit Translation Form */}
          {!showAddForm && availableLanguages.length > 0 && (
            <Button
              variant="outline"
              onClick={() => {
                setShowAddForm(true);
                // Scroll to form after it renders
                setTimeout(() => {
                  formRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }, 100);
              }}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Translation
            </Button>
          )}

          {showAddForm && (
            <div ref={formRef} className="p-3 sm:p-4 md:p-6 border-2 border-blue-300 rounded-lg space-y-4 bg-blue-50/50 scroll-mt-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-medium text-blue-900 text-sm sm:text-base flex items-center gap-2 flex-1 min-w-0">
                  {editingTranslation ? (
                    <>
                      <Edit2 className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">Edit: {SUPPORTED_LANGUAGES.find(l => l.code === editingTranslation)?.name}</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">Add New Translation</span>
                    </>
                  )}
                </h3>
                <Button variant="ghost" size="sm" onClick={handleCancel} className="flex-shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Language Selector */}
              {!editingTranslation && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2">Language</label>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="w-full p-2 sm:p-3 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Language</option>
                    {availableLanguages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name} ({lang.code})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Category Name Translation */}
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">
                  Translated Category Name
                  <span className="block sm:inline text-xs text-gray-500 sm:ml-2 mt-1 sm:mt-0">
                    (Original: {categoryName})
                  </span>
                </label>
                <Input
                  value={translationName}
                  onChange={(e) => setTranslationName(e.target.value)}
                  placeholder="Enter translated category name"
                  className="text-sm sm:text-base"
                />
              </div>

              {/* Subcategories Translation */}
              {originalSubcategories.length > 0 && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-3">
                    Translate Subcategories
                    <span className="block sm:inline text-xs text-gray-500 sm:ml-2 mt-1 sm:mt-0">
                      ({originalSubcategories.length} subcategories)
                    </span>
                  </label>
                  
                  <div className="space-y-2 sm:space-y-3 max-h-64 sm:max-h-80 md:max-h-96 overflow-y-auto pr-1">
                    {originalSubcategories.map((subcat, idx) => (
                      <div key={idx} className="p-2 sm:p-3 bg-white sm:bg-gray-50 rounded-lg border">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                          <div className="flex items-center gap-2 sm:block">
                            <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium text-blue-700">
                              {idx + 1}
                            </div>
                            <div className="text-xs sm:text-sm font-medium text-gray-700 sm:hidden truncate flex-1">
                              {subcat}
                            </div>
                          </div>
                          <div className="flex-1 space-y-1 sm:space-y-2 min-w-0">
                            <div className="text-xs sm:text-sm font-medium text-gray-700 break-words hidden sm:block">
                              {subcat}
                            </div>
                            <Input
                              value={translationSubcategories[subcat] || ""}
                              onChange={(e) => handleSubcategoryChange(subcat, e.target.value)}
                              placeholder={"Translation..."}
                              className="text-xs sm:text-sm h-8 sm:h-10"
                            />
                          </div>
                          {translationSubcategories[subcat] && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveSubcategoryTranslation(subcat)}
                              className="flex-shrink-0 h-8 w-8 p-0 sm:h-9 sm:w-9"
                            >
                              <X className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-3 p-2 sm:p-3 bg-blue-50 rounded text-xs sm:text-sm text-blue-700">
                    💡 Tip: You can translate all or some subcategories. Empty translations will be skipped.
                  </div>
                  
                  {Object.keys(translationSubcategories).length > 0 && (
                    <div className="mt-2 text-xs sm:text-sm font-medium text-green-600">
                      ✓ {Object.keys(translationSubcategories).length} of {originalSubcategories.length} subcategories translated
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                <Button variant="outline" onClick={handleCancel} className="w-full sm:w-auto">
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    editingTranslation
                      ? handleUpdateTranslation(editingTranslation)
                      : handleAddTranslation()
                  }
                  disabled={!translationName.trim() || (!editingTranslation && !selectedLanguage)}
                  className="w-full sm:w-auto"
                >
                  {editingTranslation ? "Update Translation" : "Add Translation"}
                </Button>
              </div>
            </div>
          )}

          {/* No Translations Message */}
          {existingTranslations.length === 0 && !showAddForm && (
            <div className="text-center py-8 sm:py-12 text-gray-500">
              <Languages className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm sm:text-base">No translations added yet</p>
              <p className="text-xs sm:text-sm mt-1">Click &quot;Add Translation&quot; to add translations for this category</p>
            </div>
          )}
        </div>

        {/* Footer - Close Button (Always Visible) */}
        <div className="sticky bottom-0 bg-white flex justify-end px-4 sm:px-6 py-3 sm:py-4 border-t mt-auto">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryTranslationDialog;

